import React, { useEffect, useMemo, useState } from "react";
import AvatarImg from "../components/AvatarImg";
import { useLanguage } from "../i18n/LanguageContext";
import {
  ACTIVITY_CATEGORIES,
  activityCategoryMeta,
  activityDateGroup,
  activityTimeLabel,
  buildActivityText,
  canViewActivity,
  getCachedActivityFeed,
  markActivityRead,
  subscribeActivityFeed,
} from "../services/activityService";

/* ─── Collapsible grup bileşeni ─────────────────────────────── */
function ActivityGroup({ group, activities, user, uyeler, defaultOpen, goActivity, t, lang }) {
  const [open, setOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);
  const INITIAL_LIMIT = 5;
  const displayed = open ? (showAll ? activities : activities.slice(0, INITIAL_LIMIT)) : [];
  const hasMore = activities.length > INITIAL_LIMIT && !showAll;

  return (
    <section className="activityGroup">
      <button className="activityGroupHeader" onClick={() => setOpen((o) => !o)}>
        <span className="activityGroupTitle">{group}</span>
        <div className="activityGroupMeta">
          <span className="activityGroupCount">{activities.length}</span>
          <span className={`activityGroupChevron${open ? " open" : ""}`}>›</span>
        </div>
      </button>

      {open && (
        <div className="activityGroupList">
          {displayed.map((activity) => {
            const text = buildActivityText(activity, t, lang);
            const meta = activityCategoryMeta[activity.category] || activityCategoryMeta.system;
            const initials = (activity.actorName || "FP").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
            const isUnread = !activity.readBy?.[user?.uid];
            const liveUye = uyeler.find((u) => u.uid === activity.actorId);
            const actorAvatar = liveUye?.avatar || activity.actorAvatar || null;
            const actorRenk = liveUye?.calendarColor || "#6366f1";

            return (
              <button
                key={activity.id}
                className={isUnread ? "activityRow unread" : "activityRow"}
                onClick={() => goActivity(activity)}
              >
                <AvatarImg
                  avatar={actorAvatar}
                  renk={actorRenk}
                  initials={initials}
                  size={42}
                  glow={false}
                  radius="16px"
                  className="activityAvatar"
                />
                <div className={`activityIcon ${activity.category}`}>{meta.icon}</div>
                <div className="activityRowBody">
                  <div className="activityRowTop">
                    <strong>{text.title}</strong>
                    {isUnread && <span className="activityNewBadge">{t("activity.new")}</span>}
                  </div>
                  <p>{text.message}</p>
                  <div className="activityMetaLine">
                    <span>{activityTimeLabel(activity.createdAt, lang)}</span>
                    <span>·</span>
                    <span>{t(`activity.category.${activity.category}`)}</span>
                  </div>
                </div>
                <span className="activityGo">›</span>
              </button>
            );
          })}

          {open && hasMore && (
            <button className="activityShowMore" onClick={(e) => { e.stopPropagation(); setShowAll(true); }}>
              {t("activity.showMore", { count: activities.length - INITIAL_LIMIT })}
              <span className="activityShowMoreChevron">›</span>
            </button>
          )}
        </div>
      )}
    </section>
  );
}

/* ─── Ana sayfa ─────────────────────────────────────────────── */
function ActivityFeedPage({ familyId, user, permissions, setAktifSayfa, uyeler = [] }) {
  const { t, lang } = useLanguage();
  const [activities, setActivities] = useState([]);
  const [feedReady, setFeedReady] = useState(false);
  const [filter, setFilter] = useState("all");
  const [memberFilter, setMemberFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!familyId) {
      setActivities([]);
      setFeedReady(true);
      return undefined;
    }

    const cachedActivities = getCachedActivityFeed(familyId, 120);
    if (cachedActivities) {
      setActivities(cachedActivities);
      setFeedReady(true);
    } else {
      setActivities([]);
      setFeedReady(false);
    }

    return subscribeActivityFeed(familyId, (nextActivities) => {
      setActivities(nextActivities);
      setFeedReady(true);
    }, 120);
  }, [familyId]);

  /* Aktif üyeler — aktivitelerde görünen üyeler */
  const activeMembers = useMemo(() => {
    const seen = new Map();
    (activities || []).forEach((a) => {
      if (a.actorId && !seen.has(a.actorId)) {
        const liveUye = uyeler.find((u) => u.uid === a.actorId);
        seen.set(a.actorId, {
          uid: a.actorId,
          name: liveUye ? `${liveUye.ad || ""} ${liveUye.soyad || ""}`.trim() : a.actorName || "",
          avatar: liveUye?.avatar || a.actorAvatar || null,
          renk: liveUye?.calendarColor || "#6366f1",
        });
      }
    });
    return Array.from(seen.values());
  }, [activities, uyeler]);

  const visibleActivities = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (activities || [])
      .filter((a) => canViewActivity(a, permissions, user?.uid))
      .filter((a) => filter === "all" || a.category === filter)
      .filter((a) => memberFilter === "all" || a.actorId === memberFilter)
      .filter((a) => {
        if (!q) return true;
        const text = buildActivityText(a, t, lang);
        const haystack = [text.title, text.message, a.actorName, a.category, a.type, JSON.stringify(a.payload || {})].join(" ").toLowerCase();
        return haystack.includes(q);
      });
  }, [activities, permissions, user?.uid, filter, memberFilter, search, t, lang]);

  /* İstatistikler */
  const todayCount = useMemo(() =>
    (activities || []).filter((a) => activityDateGroup(a.createdAt, lang) === t("activity.group.today")).length,
    [activities, lang, t]);

  const weekCount = useMemo(() =>
    (activities || []).filter((a) => {
      const g = activityDateGroup(a.createdAt, lang);
      return g === t("activity.group.today") || g === t("activity.group.yesterday") || g === t("activity.group.week");
    }).length,
    [activities, lang, t]);

  const mostActive = useMemo(() => {
    const counts = {};
    (activities || []).forEach((a) => {
      const uid = a.actorId;
      if (!uid) return;
      counts[uid] = (counts[uid] || 0) + 1;
    });
    const topUid = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
    if (!topUid) return "—";
    const liveUye = uyeler.find((u) => u.uid === topUid);
    if (liveUye) return `${liveUye.ad || ""} ${liveUye.soyad || ""}`.trim() || liveUye.ad || "—";
    const fromActivity = activities.find((a) => a.actorId === topUid);
    return fromActivity?.actorName || "—";
  }, [activities, uyeler]);

  const unreadCount = useMemo(() =>
    (activities || []).filter((a) => !a.readBy?.[user?.uid]).length,
    [activities, user?.uid]);

  /* Özet banner metni */
  const summaryText = useMemo(() => {
    const taskDone = (activities || []).filter((a) => a.type === "task_completed" && activityDateGroup(a.createdAt, lang) !== t("activity.group.older")).length;
    const shopDone = (activities || []).filter((a) => a.type === "shopping_item_completed" && activityDateGroup(a.createdAt, lang) !== t("activity.group.older")).length;
    const parts = [];
    if (taskDone > 0) parts.push(lang === "en" ? `${taskDone} task${taskDone > 1 ? "s" : ""} completed` : `${taskDone} görev tamamlandı`);
    if (shopDone > 0) parts.push(lang === "en" ? `${shopDone} item${shopDone > 1 ? "s" : ""} bought` : `${shopDone} ürün alındı`);
    if (parts.length === 0) return lang === "en" ? "Family activity is being tracked" : "Aile aktivitesi takip ediliyor";
    return (lang === "en" ? "This week: " : "Bu hafta: ") + parts.join(" · ");
  }, [activities, lang, t]);

  const grouped = useMemo(() => {
    const map = {};
    visibleActivities.forEach((a) => {
      const group = activityDateGroup(a.createdAt, lang);
      if (!map[group]) map[group] = [];
      map[group].push(a);
    });
    return map;
  }, [visibleActivities, lang]);

  const goActivity = async (activity) => {
    if (activity?.id && user?.uid) markActivityRead({ familyId, activityId: activity.id, uid: user.uid });
    const page = activity?.targetPage || activityCategoryMeta[activity?.category]?.page;
    if (page && page !== "ana") setAktifSayfa(page);
  };

  const filters = ACTIVITY_CATEGORIES.filter((c) => c === "all" || activityCategoryMeta[c]);
  const GROUP_ORDER = [t("activity.group.today"), t("activity.group.yesterday"), t("activity.group.week"), t("activity.group.older")];

  return (
    <div className="pageContent activityPage">
      {/* Hero */}
      <div className="activityHero">
        <div className="activityHeroGlow activityHeroGlowOne" />
        <div className="activityHeroGlow activityHeroGlowTwo" />
        <div className="activityHeroContent">
          <div className="activityHeroBadge">✨ {t("activity.heroBadge")}</div>
          <h1>{t("activity.title")}</h1>
          <p>{t("activity.subtitle")}</p>
        </div>
      </div>

      {/* Özet banner */}
      <div className="activitySummaryBanner">
        <span className="activitySummaryIcon">📊</span>
        <span className="activitySummaryText">{summaryText}</span>
      </div>

      {/* Stats */}
      <div className="activityStatsGrid">
        <div className="activityStatCard">
          <span>{t("activity.stat.today")}</span>
          <strong>{todayCount}</strong>
        </div>
        <div className="activityStatCard">
          <span>{t("activity.stat.week")}</span>
          <strong>{weekCount}</strong>
        </div>
        <div className="activityStatCard wide">
          <span>{t("activity.stat.mostActive")}</span>
          <strong>{mostActive}</strong>
        </div>
        <div className="activityStatCard">
          <span>{t("activity.stat.unread")}</span>
          <strong>{unreadCount}</strong>
        </div>
      </div>

      {/* Toolbar: arama + kategori filtresi */}
      <div className="activityToolbar">
        <div className="activitySearchWrap">
          <span>🔎</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("activity.search")}
          />
          {search.length > 0 && (
            <button className="activitySearchClear" onClick={() => setSearch("")}>×</button>
          )}
        </div>

        {/* Kategori chip'leri */}
        <div className="activityFilterChips" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          {filters.map((key) => (
            <button
              key={key}
              className={filter === key ? "activityChip active" : "activityChip"}
              onClick={() => setFilter(key)}
            >
              {key !== "all" && <span>{activityCategoryMeta[key]?.icon}</span>}
              {t(`activity.category.${key}`)}
            </button>
          ))}
        </div>

        {/* Üye filtresi — aktivitelerde görünen üyeler */}
        {activeMembers.length > 1 && (
          <div className="activityMemberFilter" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
            <button
              className={memberFilter === "all" ? "activityMemberChip active" : "activityMemberChip"}
              onClick={() => setMemberFilter("all")}
            >
              <span className="activityMemberChipAll">👥</span>
              <span>{lang === "en" ? "All" : "Tümü"}</span>
            </button>
            {activeMembers.map((m) => (
              <button
                key={m.uid}
                className={memberFilter === m.uid ? "activityMemberChip active" : "activityMemberChip"}
                onClick={() => setMemberFilter(m.uid)}
              >
                <AvatarImg avatar={m.avatar} renk={m.renk} initials={(m.name || "FP").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()} size={22} glow={false} radius="8px" 
                      uid={m.uid || m.id}
                      isOnline={m.isOnline === true || m.online === true}
                    />
                <span>{m.name.split(" ")[0]}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="activityTimelineShell">
        {!feedReady ? (
          <div className="activitySkeletonList" aria-hidden="true">
            {Array.from({ length: 5 }).map((_, index) => (
              <div className="activitySkeletonRow" key={index}>
                <span className="activitySkeletonAvatar" />
                <div className="activitySkeletonBody">
                  <span className="activitySkeletonLine wide" />
                  <span className="activitySkeletonLine" />
                </div>
              </div>
            ))}
          </div>
        ) : visibleActivities.length === 0 ? (
          <div className="activityEmpty">
            <div className="activityEmptyIcon">🌙</div>
            <h3>{t("activity.emptyTitle")}</h3>
            <p>{t("activity.emptyText")}</p>
          </div>
        ) : (
          <div className="activityTimeline">
            {GROUP_ORDER.filter((g) => grouped[g]?.length).map((group, i) => (
              <ActivityGroup
                key={group}
                group={group}
                activities={grouped[group]}
                user={user}
                uyeler={uyeler}
                defaultOpen={i === 0}
                goActivity={goActivity}
                t={t}
                lang={lang}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ActivityFeedPage;